import * as PIXI from 'pixi.js';
import * as PrefabManager from '../PrefabManager';
import {game} from '../../Game';
import {Settings} from '../../Settings';
import * as AudioManager from '../../utils/AudioManager';
import {
	drawObjectAdding,
	stopCustomBehaviour,
} from '../misc/CustomEditorBehavior';
import {PortalBridge} from './PortalBridge';
import {createPortalMesh} from './PortalShader';
import {
	getPortalOpeningTransform,
	PORTAL_OPEN_DURATION,
	PortalOpeningBurst,
} from './PortalEffects';
import {
	bodyVelocityAtPoint,
	fixtureCanHostPortal,
	fixtureLiesBehindPortal,
	fixtureLiesInFrontOfPortal,
	getFixturePortalEntrySide,
	getFixturePortalSensorEntryFace,
	getPortalCandidateSensorFixtures,
	resolvePortalWheelCapContact,
} from './portalCollision';
import {
	PORTAL_APERTURE_HALF_LENGTH,
	PORTAL_HEIGHT,
	PORTAL_SURFACE_HALF_THICKNESS,
} from './portalMath';
import {getPortalIntersectionSegments} from './PortalIntersectionParticles';
import {setStrokeStyle} from '../../utils/pixiGraphics';
import {simulationFrame} from '../../utils/SimulationClock';

const {getPointer, NULL} = Box2D;
const portalPairs = new Map();
const PORTAL_WIDTH = PORTAL_SURFACE_HALF_THICKNESS * 2;
const PORTAL_HALF_WIDTH = PORTAL_WIDTH / 2;
const DIRECTION_ARROW_START = PORTAL_HALF_WIDTH * Settings.PTM + 12;
const DIRECTION_ARROW_SHAFT_END = DIRECTION_ARROW_START + 72;
const DIRECTION_ARROW_TIP = DIRECTION_ARROW_START + 132;

const registerPair = portal => {
	const pairId = portal.prefabObject.settings.pairId;
	if(!pairId || !portal.active) return;
	if(portal.registeredPairId && portal.registeredPairId !== pairId) {
		unregisterPair(portal);
	}
	if(!portalPairs.has(pairId)) portalPairs.set(pairId, new Set());
	const endpoints = portalPairs.get(pairId);
	endpoints.add(portal);
	portal.registeredPairId = pairId;
	const available = [...endpoints].filter(endpoint =>
		!endpoint.destroyed && endpoint.active);
	if(available.length >= 2) available[0].linkPortal(available[1]);
};

const unregisterPair = portal => {
	const pairId = portal.registeredPairId;
	if(!pairId) return;
	const endpoints = portalPairs.get(pairId);
	if(endpoints) {
		endpoints.delete(portal);
		if(!endpoints.size) portalPairs.delete(pairId);
	}
	portal.registeredPairId = null;
};

class Portal extends PrefabManager.basePrefab {
	constructor(target) {
		super(target);
		this.prefabObject.settings = this.prefabObject.settings || {};
		// Strip settings serialized by older builds. They are no longer exposed or
		// used: ribbon width is fixed in the shader and exits preserve velocity.
		delete this.prefabObject.settings.ribbonWidth;
		delete this.prefabObject.settings.exitBoost;
		this.body = this.lookupObject.portal;
		this.base = this.body;
		this.isPortal = true;
		this.body.isPortalSurface = true;
		this.active = true;
		this.linkedPortal = null;
		this.registeredPairId = null;
		this.connectedPortal = null;
		this.bridge = null;
		this.hostBody = null;
		this.hostFixture = null;
		this.hostFixtures = [];
		this.hostFixtureKeys = new Set();
		this.hostLocalPosition = null;
		this.hostLocalAngle = 0;
		this.initialized = false;
		this.apertureHalfLength = PORTAL_APERTURE_HALF_LENGTH;
		this.surfaceHalfThickness = PORTAL_HALF_WIDTH;
		this.volumeSensorFixtureKey = null;
		this.crossLineFixtureKey = null;
		this.capFixtureKeys = new Set();
		this.frontSensorFixtureKeys = new Set();
		this.sensorFixtures = new Map();
		this.directionArrow = null;
		this.portalVisual = null;
		this.portalShader = null;
		this.openingElapsed = null;
		this.openingBurst = null;
		this.pendingOpeningSound = false;
		this.buzzVolume = 0;
		this.audioKey = `portal-${this.prefabObject.instanceID}`;
	}

	postConstructor() {
		super.postConstructor();
		this.createPortalVisual();
		// Prefab init() only runs when the editor enters test mode. Build this
		// editor-only overlay at construction time so it is present while editing.
		this.createDirectionArrow();
	}

	init() {
		super.init();
		this.prefabObject.settings = Object.assign(
			{},
			Portal.settings,
			this.prefabObject.settings || {},
		);
		delete this.prefabObject.settings.ribbonWidth;
		delete this.prefabObject.settings.exitBoost;
		delete this.prefabObject.settings.portalTarget;
		this.initialized = true;
		this.destroyed = false;
		this.body.SetType(Box2D.b2_staticBody);
		this.body.SetAwake(true);
		this.body.isPortalSurface = true;
		this.body.portalEndpoint = this;
		this.apertureHalfLength = PORTAL_APERTURE_HALF_LENGTH;
		this.surfaceHalfThickness = PORTAL_HALF_WIDTH;
		this.rebuildPortalFixtures();
		this.resolveHostFixtures();

		this.createPortalVisual();
		this.setColor(this.prefabObject.settings.color);
		this.createDirectionArrow();
		this.setActive(this.prefabObject.settings.active !== false, true);
		AudioManager.preloadSFX('portal_open');
	}

	initContactListener() {
		super.initContactListener();
		this.contactListener.BeginContact = contact => {
			if(!this.bridge) return;
			const fixtureA = contact.GetFixtureA();
			const fixtureB = contact.GetFixtureB();
			const bodyA = fixtureA.GetBody();
			const portalIsA = getPointer(bodyA) === getPointer(this.body);
			const portalFixture = portalIsA ? fixtureA : fixtureB;
			if(!this.isVolumeSensorFixture(portalFixture)) return;
			const otherFixture = portalIsA ? fixtureB : fixtureA;
			const otherBody = otherFixture.GetBody();
			if(otherBody === this.body || otherBody.isPortalSurface) return;
			// An emerging island's shadow overlaps the exit volume by construction.
			if(otherBody.isPortalGhost) return;
			if(otherBody.GetType() === Box2D.b2_staticBody) return;
			// An attached rope's long bend fixture can span the whole portal before
			// the rider gets anywhere near it. It belongs to the constraint, not to
			// a traveller, so let a real rider/vehicle fixture seed the island.
			if(otherBody.isRopeEnd) return;
			if(otherBody.mySprite?.data?.type === game.editor.object_TRIGGER) return;
			const key = this.fixtureKey(otherFixture);
			this.sensorFixtures.set(key, otherFixture);
			const existing = otherBody._portalTransitCandidate;
			if(existing?.source === this) {
				this.bridge.markSourceFixtureInside(existing, otherFixture);
				return;
			}
			if(getFixturePortalSensorEntryFace(Box2D, otherFixture, this) === 'front') {
				this.frontSensorFixtureKeys.add(key);
				// Start mirroring at the front of the volume instead of waiting for
				// the fixture to touch the zero-width cross-line. Editor portals can
				// sit a small distance inside their host surface; in that layout the
				// host otherwise stops a slow or constrained body before the cross-line
				// contact can ever admit it. The finite sensor face still provides the
				// same one-way/aperture guard, and host contacts only open for fixtures
				// explicitly admitted to this candidate.
				const candidate = this.bridge.beginTransit(
					this,
					otherBody,
					simulationFrame(),
					otherFixture,
					true,
				);
				if(candidate?.source === this) this.admitOverlappingIslandFixtures(candidate);
			} else {
				this.frontSensorFixtureKeys.delete(key);
			}
		};
		this.contactListener.EndContact = contact => {
			if(!this.bridge) return;
			const fixtureA = contact.GetFixtureA();
			const fixtureB = contact.GetFixtureB();
			const bodyA = fixtureA.GetBody();
			const portalIsA = getPointer(bodyA) === getPointer(this.body);
			const portalFixture = portalIsA ? fixtureA : fixtureB;
			if(!this.isVolumeSensorFixture(portalFixture)) return;
			const otherFixture = portalIsA ? fixtureB : fixtureA;
			if(otherFixture.GetBody().isRopeEnd || otherFixture.GetBody().isPortalGhost) return;
			const key = this.fixtureKey(otherFixture);
			this.frontSensorFixtureKeys.delete(key);
			this.sensorFixtures.delete(key);
			this.bridge.endTransitContact(this, otherFixture.GetBody(), otherFixture);
		};
		this.contactListener.PreSolve = contact => {
			if(!this.bridge) return;
			const fixtureA = contact.GetFixtureA();
			const fixtureB = contact.GetFixtureB();
			const portalIsA = getPointer(fixtureA.GetBody()) === getPointer(this.body);
			const portalFixture = portalIsA ? fixtureA : fixtureB;
			const otherFixture = portalIsA ? fixtureB : fixtureA;
			// A copy of this side rests on the lips like a straddling body; it
			// passes the cross-line and other portals' rims freely. The bridge
			// normally settles this before the event reaches the prefab.
			if(otherFixture.GetBody().isPortalGhost) {
				if(otherFixture.GetBody()._portalGhostSide === this &&
					this.capFixtureKeys.has(this.fixtureKey(portalFixture))) {
					resolvePortalWheelCapContact(Box2D, contact, portalFixture, otherFixture);
				} else {
					contact.SetEnabled(false);
				}
				return;
			}
			if(this.capFixtureKeys.has(this.fixtureKey(portalFixture))) {
				// A wheel rolling over the front of a floor portal has not crossed
				// its plane yet. The sensor-depth cap must not act as a raised curb.
				// Once the fixture straddles the plane, caps still enforce the opening.
				const candidate = otherFixture.GetBody()._portalTransitCandidate;
				const isTraveller = candidate?.source === this &&
					candidate.admittedFixtureKeys.has(this.fixtureKey(otherFixture));
				// Ownership can move before the rear wheel crosses. That hidden
				// remainder is still simulated at the other portal, not against caps
				// behind this one. A part that crosses the plane outside the opening
				// passes the portal's end, which only a wheel's rim can touch.
				const isCircle = otherFixture.GetShape().GetType() === Box2D.b2Shape.e_circle;
				if(fixtureLiesInFrontOfPortal(Box2D, otherFixture, this) ||
					(isTraveller && fixtureLiesBehindPortal(Box2D, otherFixture, this)) ||
					(!isTraveller && !isCircle)) {
					contact.SetEnabled(false);
				} else {
					resolvePortalWheelCapContact(Box2D, contact, portalFixture, otherFixture);
				}
				return;
			}
			if(!this.isBackstopFixture(portalFixture)) return;

			const otherBody = otherFixture.GetBody();
			if(otherBody.GetType() === Box2D.b2_staticBody) return;
			if(otherBody.isRopeEnd) {
				contact.SetEnabled(false);
				return;
			}
			const key = this.fixtureKey(otherFixture);
			const existing = otherBody._portalTransitCandidate;
			const alreadyAdmitted = existing?.source === this &&
				existing.admittedFixtureKeys.has(key);
			// A fast body (such as a portal projectile) can reach the cross-line in
			// the same step it entered the volume, before the sensor has registered
			// its front entry. Judge it by its own geometry and motion instead.
			if(!alreadyAdmitted && !this.frontSensorFixtureKeys.has(key) &&
				getFixturePortalEntrySide(Box2D, otherFixture, this) !== 'front') {
				return;
			}
			const candidate = alreadyAdmitted ? existing : this.bridge.beginTransit(
				this,
				otherBody,
				simulationFrame(),
				otherFixture,
				true,
			);
			if(candidate?.source === this) this.admitOverlappingIslandFixtures(candidate);
			// The cross-line is a solid back face by default. Only an admitted
			// front-face transit opens it, including when that transit backs out.
			if(candidate?.source === this &&
				candidate.admittedFixtureKeys.has(this.fixtureKey(otherFixture))) {
				contact.SetEnabled(false);
			}
		};
	}

	admitOverlappingIslandFixtures(candidate) {
		for(const fixture of getPortalCandidateSensorFixtures(
			this.sensorFixtures.values(),
			candidate,
		)) {
			this.bridge.markSourceFixtureInside(candidate, fixture);
		}
	}

	fixtureKey(fixture) {
		return fixture ? getPointer(fixture) : null;
	}

	isVolumeSensorFixture(fixture) {
		return this.fixtureKey(fixture) === this.volumeSensorFixtureKey;
	}

	isBackstopFixture(fixture) {
		return this.fixtureKey(fixture) === this.crossLineFixtureKey;
	}

	createPortalFixture(shape, sensor) {
		const fixtureDef = new Box2D.b2FixtureDef();
		fixtureDef.set_shape(shape);
		fixtureDef.set_density(0);
		fixtureDef.set_friction(0.2);
		fixtureDef.set_restitution(0.05);
		fixtureDef.set_isSensor(sensor);
		const filter = fixtureDef.get_filter();
		if(sensor) {
			filter.set_categoryBits(game.editor.MASKBIT_TRIGGER);
			filter.set_maskBits(
				game.editor.MASKBIT_NORMAL |
				game.editor.MASKBIT_FIXED |
				game.editor.MASKBIT_ONLY_US |
				game.editor.MASKBIT_CHARACTER |
				game.editor.MASKBIT_EVERYTHING_BUT_US |
				game.editor.MASKBIT_NOTHING
			);
		} else {
			filter.set_categoryBits(game.editor.MASKBIT_FIXED);
			filter.set_maskBits(
				game.editor.MASKBIT_NORMAL |
				game.editor.MASKBIT_FIXED |
				game.editor.MASKBIT_CHARACTER |
				game.editor.MASKBIT_EVERYTHING_BUT_US |
				game.editor.MASKBIT_TRIGGER |
				game.editor.MASKBIT_PHYSICS_CULL
			);
		}
		const fixture = this.body.CreateFixture(fixtureDef);
		Box2D.destroy(fixtureDef);
		return fixture;
	}

	createPortalEdge(from, to, sensor = false) {
		const shape = new Box2D.b2EdgeShape();
		const vertexA = new Box2D.b2Vec2(from.x, from.y);
		const vertexB = new Box2D.b2Vec2(to.x, to.y);
		shape.SetTwoSided(vertexA, vertexB);
		const fixture = this.createPortalFixture(shape, sensor);
		Box2D.destroy(vertexB);
		Box2D.destroy(vertexA);
		Box2D.destroy(shape);
		return fixture;
	}

	rebuildPortalFixtures() {
		this.frontSensorFixtureKeys.clear();
		this.sensorFixtures.clear();
		const fixtures = [];
		for(let fixture = this.body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
			fixtures.push(fixture);
		}
		fixtures.forEach(fixture => {
			// The wrapper is reused for the next fixture at this address.
			delete fixture._portalBackstop;
			this.body.DestroyFixture(fixture);
		});

		const volumeShape = new Box2D.b2PolygonShape();
		volumeShape.SetAsBox(this.surfaceHalfThickness, this.apertureHalfLength);
		const volumeFixture = this.createPortalFixture(volumeShape, true);
		Box2D.destroy(volumeShape);
		this.volumeSensorFixtureKey = this.fixtureKey(volumeFixture);

		const crossLine = this.createPortalEdge(
			{x: 0, y: -this.apertureHalfLength},
			{x: 0, y: this.apertureHalfLength},
		);
		crossLine._portalBackstop = true;
		this.crossLineFixtureKey = this.fixtureKey(crossLine);

		this.capFixtureKeys.clear();
		for(const y of [-this.apertureHalfLength, this.apertureHalfLength]) {
			const cap = this.createPortalEdge(
				{x: -this.surfaceHalfThickness, y},
				{x: this.surfaceHalfThickness, y},
			);
			this.capFixtureKeys.add(this.fixtureKey(cap));
		}

		this.updatePortalVisual();
	}

	updatePortalVisual() {
		const data = this.body?.mySprite?.data;
		if(!data?.vertices?.length) return;
		const vertices = [
			{x: -PORTAL_HALF_WIDTH, y: PORTAL_APERTURE_HALF_LENGTH},
			{x: -PORTAL_HALF_WIDTH, y: -PORTAL_APERTURE_HALF_LENGTH},
			{x: PORTAL_HALF_WIDTH, y: -PORTAL_APERTURE_HALF_LENGTH},
			{x: PORTAL_HALF_WIDTH, y: PORTAL_APERTURE_HALF_LENGTH},
		];
		if(Array.isArray(data.vertices[0]?.[0])) data.vertices[0][0] = vertices;
		else data.vertices[0] = vertices;
		game.editor.updateBodyShapes(this.body);
		this.hidePortalBodyGraphic();
	}

	createPortalVisual() {
		if(this.portalVisual || !this.body?.mySprite) return;
		const seed = (((this.prefabObject.instanceID || 0) + 1) * 0.754877666) % 1;
		const {mesh, shader} = createPortalMesh({
			height: PORTAL_HEIGHT * Settings.PTM,
			color: this.prefabObject.settings?.color || Portal.settings.color,
			seed,
		});
		this.portalVisual = mesh;
		this.portalShader = shader;
		this.body.mySprite.addChild(mesh);
		this.hidePortalBodyGraphic();
	}

	hidePortalBodyGraphic() {
		if(this.body?.originalGraphic) this.body.originalGraphic.visible = false;
	}

	destroyPortalVisual() {
		const shader = this.portalShader;
		if(this.portalVisual && !this.portalVisual.destroyed) {
			this.portalVisual.onRender = null;
			this.portalVisual.removeFromParent();
			this.portalVisual.destroy({texture: false, textureSource: false});
		}
		if(shader) shader.destroy();
		this.portalVisual = null;
		this.portalShader = null;
	}

	startPortalOpening() {
		if(!this.portalVisual || !this.body?.mySprite) return;
		this.destroyPortalOpening();
		this.openingElapsed = 0;
		const visualHeight = PORTAL_HEIGHT * Settings.PTM * 1.12;
		const visualWidth = PORTAL_HEIGHT * Settings.PTM * 0.252;
		this.openingBurst = new PortalOpeningBurst({
			parent: this.body.mySprite,
			color: this.prefabObject.settings?.color || Portal.settings.color,
			width: visualWidth,
			height: visualHeight,
		});
		const transform = this.applyPortalOpening(0);
		this.openingBurst.update(0, 0, transform);
	}

	applyPortalOpening(progress) {
		const transform = getPortalOpeningTransform(progress);
		if(this.portalVisual) {
			this.portalVisual.alpha = transform.alpha;
			this.portalVisual.scale.set(transform.scaleX, transform.scaleY);
		}
		this.portalShader?.setOpenProgress(progress);
		return transform;
	}

	updatePortalOpening() {
		if(this.openingElapsed === null) return;
		const deltaSeconds = Math.max(
			0,
			Math.min(0.06, Number(game.editor.deltaTimeSeconds) || 0),
		);
		this.openingElapsed += deltaSeconds;
		const progress = Math.min(1, this.openingElapsed / PORTAL_OPEN_DURATION);
		const transform = this.applyPortalOpening(progress);
		const effectAlive = this.openingBurst?.update(
			deltaSeconds,
			progress,
			transform,
		);
		if(progress >= 1) this.applyPortalOpening(1);
		if(effectAlive !== false) return;
		this.destroyPortalOpening();
		this.openingElapsed = null;
	}

	destroyPortalOpening() {
		this.openingBurst?.destroy();
		this.openingBurst = null;
	}

	createDirectionArrow() {
		if(this.directionArrow || !this.body) return;
		this.directionArrow = new PIXI.Graphics();
		this.directionArrow.label = 'portal-front-direction';
		this.directionArrow.eventMode = 'none';
		this.directionArrow.onRender = () => this.syncDirectionArrowTransform();
		// This is an editor overlay rather than a body child: it renders above the
		// level without participating in editor object IDs or serialization.
		game.editor.container.addChild(this.directionArrow);
		this.drawDirectionArrow();
		this.syncDirectionArrowTransform();
		this.updateDirectionArrowVisibility();
	}

	drawDirectionArrow() {
		if(!this.directionArrow) return;
		const outer = [
			DIRECTION_ARROW_START, -19,
			DIRECTION_ARROW_SHAFT_END, -19,
			DIRECTION_ARROW_SHAFT_END, -45,
			DIRECTION_ARROW_TIP, 0,
			DIRECTION_ARROW_SHAFT_END, 45,
			DIRECTION_ARROW_SHAFT_END, 19,
			DIRECTION_ARROW_START, 19,
		];
		const inner = [
			DIRECTION_ARROW_START + 5, -13,
			DIRECTION_ARROW_SHAFT_END + 2, -13,
			DIRECTION_ARROW_SHAFT_END + 2, -32,
			DIRECTION_ARROW_TIP - 11, 0,
			DIRECTION_ARROW_SHAFT_END + 2, 32,
			DIRECTION_ARROW_SHAFT_END + 2, 13,
			DIRECTION_ARROW_START + 5, 13,
		];
		this.directionArrow.clear();
		this.directionArrow.poly(outer, true).fill({color: 0x111111, alpha: 0.9});
		this.directionArrow.poly(inner, true).fill({
			color: this.prefabObject.settings?.color || Portal.settings.color,
			alpha: 0.95,
		});
	}

	updateDirectionArrowVisibility() {
		if(!this.directionArrow) return;
		this.directionArrow.visible =
			game.gameState === game.GAMESTATE_EDITOR && !game.run;
	}

	syncDirectionArrowTransform() {
		if(!this.directionArrow || !this.body) return;
		const position = this.body.GetPosition();
		this.directionArrow.position.set(
			position.get_x() * Settings.PTM,
			position.get_y() * Settings.PTM,
		);
		this.directionArrow.rotation = this.body.GetAngle();
		const spriteScale = this.body.mySprite?.scale;
		this.directionArrow.scale.set(spriteScale?.x || 1, spriteScale?.y || 1);
	}

	destroyDirectionArrow() {
		if(this.directionArrow) {
			if(!this.directionArrow.destroyed) {
				this.directionArrow.removeFromParent();
				this.directionArrow.destroy();
			}
			this.directionArrow = null;
		}
	}

	drawDebugEditor() {
		const linkedPortal = this.getLinkedPortal();
		if(!linkedPortal) return;
		const editor = game.editor;
		const source = this.body?.mySprite;
		const target = linkedPortal.body?.mySprite;
		if(!source || !target) return;
		setStrokeStyle(editor.debugGraphics, 2,
			this.prefabObject.settings.color || '#0093ff', 0.85);
		editor.debugGraphics.moveTo(
			source.x * editor.cameraHolder.scale.x + editor.cameraHolder.x,
			source.y * editor.cameraHolder.scale.y + editor.cameraHolder.y,
		);
		editor.debugGraphics.lineTo(
			target.x * editor.cameraHolder.scale.x + editor.cameraHolder.x,
			target.y * editor.cameraHolder.scale.y + editor.cameraHolder.y,
		);
		editor.debugGraphics.stroke();
	}

	onEditorDelete() {
		this.setLinkedPortal(null);
		this.destroyPortalOpening();
		this.destroyPortalVisual();
		this.destroyDirectionArrow();
	}

	isLinkedPortalAvailable(portal = this.linkedPortal) {
		return Boolean(portal && portal !== this && portal.isPortal &&
			!portal.destroyed && portal.body && !portal.body.destroyed);
	}

	getLinkedPortal() {
		if(this.isLinkedPortalAvailable()) return this.linkedPortal;
		this.linkedPortal = null;
		if(this.prefabObject.settings) this.prefabObject.settings.linkedPortalId = null;
		return null;
	}

	serializeProps() {
		const linkedPortal = this.getLinkedPortal();
		const targetDisplay = linkedPortal?.body?.mySprite;
		if(targetDisplay?.parent) {
			game.editor.updateObject(targetDisplay, targetDisplay.data);
			this.prefabObject.settings.linkedPortalId = [
				targetDisplay.parent.getChildIndex(targetDisplay),
			];
			return;
		}
		this.prefabObject.settings.linkedPortalId = null;
	}

	initializeProps() {
		const linkedPortalId = this.prefabObject.settings.linkedPortalId;
		if(Array.isArray(linkedPortalId)) {
			const targetDisplay = game.editor.textures.children[linkedPortalId[0]];
			const targetPortal = targetDisplay?.myBody ?
				game.editor.retrieveClassFromBody(targetDisplay.myBody) : null;
			if(this.isLinkedPortalAvailable(targetPortal)) {
				this.setLinkedPortal(targetPortal);
				return;
			}
		}

		// Migrate the old free-text pair ID when an existing level is opened in
		// the editor. Runtime-created hat portals still use that ID internally.
		const legacyPairId = this.prefabObject.settings.pairId;
		if(!legacyPairId) return;
		const targetPortal = Object.values(game.editor.activePrefabs)
			.map(prefab => prefab.class)
			.find(portal => portal !== this && portal?.isPortal &&
				portal.prefabObject.settings.pairId === legacyPairId);
		if(targetPortal) this.setLinkedPortal(targetPortal);
	}

	setLinkedPortal(targetPortal) {
		if(!this.isLinkedPortalAvailable(targetPortal)) targetPortal = null;
		const previousPortal = this.getLinkedPortal();
		if(previousPortal === targetPortal) {
			this.serializeProps();
			return;
		}

		const targetPreviousPortal = targetPortal?.getLinkedPortal?.() || null;
		const affectedPortals = new Set([
			this,
			previousPortal,
			targetPortal,
			targetPreviousPortal,
		].filter(Boolean));
		for(const portal of affectedPortals) {
			unregisterPair(portal);
			if(portal.initialized) portal.detachBridge();
		}

		if(previousPortal?.linkedPortal === this) previousPortal.linkedPortal = null;
		if(targetPreviousPortal && targetPreviousPortal !== this &&
			targetPreviousPortal.linkedPortal === targetPortal) {
			targetPreviousPortal.linkedPortal = null;
		}
		this.linkedPortal = null;
		if(targetPortal) {
			this.linkedPortal = targetPortal;
			targetPortal.linkedPortal = this;
		}

		for(const portal of affectedPortals) {
			portal.prefabObject.settings.pairId = '';
			portal.serializeProps();
		}
		for(const portal of affectedPortals) portal.tryLinkPortal();
	}

	linkPortalTarget(targetDisplay) {
		const targetBody = targetDisplay?.myBody || targetDisplay;
		const targetPortal = targetBody?.mySprite ?
			game.editor.retrieveClassFromBody(targetBody) : null;
		this.setLinkedPortal(targetPortal);
		game.editor.updateSelection();
	}

	tryLinkPortal() {
		if(!this.initialized || !this.active || this.destroyed || this.bridge) return;
		const linkedPortal = this.getLinkedPortal();
		if(linkedPortal) {
			if(linkedPortal.initialized && linkedPortal.active) this.linkPortal(linkedPortal);
			return;
		}
		registerPair(this);
	}

	setActive(active, replayAnimation = false) {
		const nextActive = active !== false;
		const changed = this.active !== nextActive;
		this.active = nextActive;
		this.prefabObject.settings.active = nextActive;
		if(!this.initialized) return;

		if(!nextActive) {
			this.pendingOpeningSound = false;
			if(changed) this.playPortalSound('portal_close');
			this.stopPortalBuzz();
			unregisterPair(this);
			this.detachBridge();
			this.body.SetEnabled(false);
			if(this.portalVisual) this.portalVisual.visible = false;
			this.destroyPortalOpening();
			this.openingElapsed = null;
			return;
		}

		this.body.SetEnabled(true);
		if(this.portalVisual) this.portalVisual.visible = true;
		if(changed || replayAnimation) {
			this.startPortalOpening();
			// Level prefabs initialize before game.run becomes true. Play their
			// opening cue on the first gameplay update instead of losing it.
			this.pendingOpeningSound = !game.run;
			this.playPortalSound('portal_open');
		}
		this.tryLinkPortal();
	}

	set(property, value) {
		// Older levels may still contain these settings. Drop them instead of
		// preserving a hidden configuration that no longer affects the portal.
		if(property === 'ribbonWidth' || property === 'exitBoost') {
			delete this.prefabObject.settings[property];
			return;
		}
		if(property === 'apertureHeight' || property === 'portalDepth') {
			delete this.prefabObject.settings[property];
			this.apertureHalfLength = PORTAL_APERTURE_HALF_LENGTH;
			this.surfaceHalfThickness = PORTAL_HALF_WIDTH;
			if(this.initialized) this.rebuildPortalFixtures();
			else this.updatePortalVisual();
			return;
		}
		if(property === 'active') {
			this.setActive(value, true);
			return;
		}
		const oldPairId = this.prefabObject.settings.pairId;
		super.set(property, value);
		if(property === 'color') this.setColor(value);
		if(property === 'pairId' && this.initialized && oldPairId !== value) {
			unregisterPair(this);
			this.detachBridge();
			this.tryLinkPortal();
		}
	}

	setColor(color) {
		if(!color || !this.body?.mySprite?.data) return;
		this.prefabObject.settings.color = color;
		this.body.mySprite.data.colorFill = [color];
		game.editor.updateBodyShapes(this.body);
		this.hidePortalBodyGraphic();
		this.portalShader?.setColor(color);
		this.openingBurst?.setColor(color);
		this.drawDirectionArrow();
	}

	attachToHost(hostBody, hostFixture = null, hostFixtures = null) {
		if(!hostBody || hostBody === this.body || hostBody.destroyed) return;
		this.hostBody = hostBody;
		this.hostFixture = hostFixture;
		this.hostFixtures = hostFixtures?.length ? [...hostFixtures] :
			(hostFixture ? [hostFixture] : []);
		this.hostFixtureKeys.clear();
		if(this.hostFixtures.length) {
			for(const fixture of this.hostFixtures) {
				this.hostFixtureKeys.add(this.fixtureKey(fixture));
			}
		} else {
			for(let fixture = hostBody.GetFixtureList();
				getPointer(fixture) !== getPointer(NULL);
				fixture = fixture.GetNext()) {
				this.hostFixtureKeys.add(this.fixtureKey(fixture));
			}
		}
		const localPosition = hostBody.GetLocalPoint(this.body.GetPosition());
		this.hostLocalPosition = {x: localPosition.get_x(), y: localPosition.get_y()};
		this.hostLocalAngle = this.body.GetAngle() - hostBody.GetAngle();
		// A portal on a moving body follows it kinematically. The body then
		// carries the host's velocity through each step instead of being
		// teleported after it, so travellers and the ghost geometry see a portal
		// that really moves. Host contacts are dropped in PreSolve.
		if(hostBody.GetType() !== Box2D.b2_staticBody) {
			this.body.SetType(Box2D.b2_kinematicBody);
		}
	}

	detachFromHost() {
		this.hostBody = null;
		this.hostFixture = null;
		this.hostFixtures = [];
		this.hostLocalPosition = null;
		this.hostLocalAngle = 0;
		this.hostFixtureKeys.clear();
		if(this.body && !this.body.destroyed &&
			this.body.GetType() === Box2D.b2_kinematicBody) {
			const zero = new Box2D.b2Vec2(0, 0);
			this.body.SetLinearVelocity(zero);
			this.body.SetAngularVelocity(0);
			Box2D.destroy(zero);
		}
	}

	resolveHostFixtures() {
		this.hostFixtureKeys.clear();
		if(this.hostFixtures.length) {
			for(const fixture of this.hostFixtures) {
				this.hostFixtureKeys.add(this.fixtureKey(fixture));
			}
			return;
		}
		if(this.hostFixture) {
			this.hostFixtureKeys.add(this.fixtureKey(this.hostFixture));
			return;
		}
		if(this.hostBody) {
			for(let fixture = this.hostBody.GetFixtureList();
				getPointer(fixture) !== getPointer(NULL);
				fixture = fixture.GetNext()) {
				this.hostFixtureKeys.add(this.fixtureKey(fixture));
			}
			return;
		}

		// Editor portals have no bullet-impact metadata. Infer only a static
		// fixture crossing the portal centre; a floor touching a cap or a dynamic
		// object already in transit must never become part of the portal opening.
		for(let body = game.world.GetBodyList();
			getPointer(body) !== getPointer(NULL);
			body = body.GetNext()) {
			if(body === this.body || body.destroyed || body.isPortalSurface ||
				body.GetType() !== Box2D.b2_staticBody ||
				body.mySprite?.data?.type === game.editor.object_TRIGGER) {
				continue;
			}
			for(let fixture = body.GetFixtureList();
				getPointer(fixture) !== getPointer(NULL);
				fixture = fixture.GetNext()) {
				if(fixtureCanHostPortal(Box2D, fixture, this)) {
					this.hostFixtureKeys.add(this.fixtureKey(fixture));
				}
			}
		}
	}

	updateHostTransform() {
		// A host that was destroyed must not leave a kinematic portal drifting.
		if(this.hostBody?.destroyed) this.detachFromHost();
		if(!this.hostBody || !this.hostLocalPosition) return;
		const localPosition = new Box2D.b2Vec2(this.hostLocalPosition.x, this.hostLocalPosition.y);
		const worldPosition = this.hostBody.GetWorldPoint(localPosition);
		if(this.body.GetType() === Box2D.b2_kinematicBody) {
			const velocity = bodyVelocityAtPoint(this.hostBody, {
				x: worldPosition.get_x(),
				y: worldPosition.get_y(),
			});
			const linearVelocity = new Box2D.b2Vec2(velocity.x, velocity.y);
			this.body.SetLinearVelocity(linearVelocity);
			this.body.SetAngularVelocity(this.hostBody.GetAngularVelocity());
			Box2D.destroy(linearVelocity);
		}
		this.body.SetTransform(worldPosition, this.hostBody.GetAngle() + this.hostLocalAngle);
		Box2D.destroy(localPosition);
	}

	detachBridge() {
		const bridge = this.bridge;
		if(!bridge) return;
		const endpointA = bridge.endpointA;
		const endpointB = bridge.endpointB;
		bridge.destroy();
		for(const endpoint of [endpointA, endpointB]) {
			if(!endpoint) continue;
			endpoint.stopPortalBuzz();
			if(endpoint.bridge === bridge) endpoint.bridge = null;
			if(endpoint.connectedPortal === (endpoint === endpointA ? endpointB : endpointA)) {
				endpoint.connectedPortal = null;
			}
		}
	}

	linkPortal(portal) {
		if(!portal || portal === this || this.destroyed || portal.destroyed ||
			!this.active || !portal.active) return;
		if(this.bridge && this.bridge === portal.bridge) return;
		this.detachBridge();
		portal.detachBridge();

		const onTransitTransferred = (_candidate, bodies) => {
			const teleportedClasses = new Set();
			for(const body of bodies) {
				const bodyClass = game.editor.retrieveSubClassFromBody(body);
				if(!bodyClass?.setTeleported || teleportedClasses.has(bodyClass)) continue;
				teleportedClasses.add(bodyClass);
				bodyClass.setTeleported();
			}
		};
		const bridge = new PortalBridge(this, portal, {
			Box2D,
			world: game.world,
			renderParent: game.editor.container,
			ptm: Settings.PTM,
			getFrame: () => simulationFrame(),
			// Far-side PostSolve events carry the contact impulse as a third
			// argument, exactly like the editor's own listener does.
			dispatchContactEvent: (name, contact, secondParam) =>
				game.editor.B2dEditorContactListener.BubbleEvent(name, contact, secondParam),
			onBodyTeleported: body => game.editor.updateBodyPosition(body),
			onTransitCommitted: onTransitTransferred,
			onTransitRebased: onTransitTransferred,
			// When a straddling island comes to rest, let the drivable part be the
			// one on the floor so the player can ride it out.
			prefersRestingBody: body => Boolean(body.isVehiclePart),
		});
		this.connectedPortal = portal;
		portal.connectedPortal = this;
		this.bridge = bridge;
		portal.bridge = bridge;
		// Static/editor portals link before the very first physics step. Seed any
		// object intentionally placed through their front plane so Box2D cannot
		// eject it while constructing the initial contact graph. Portals shot during
		// play keep the normal sensor-entry rules.
		if(!game.run) bridge.bootstrapInitialTransits(simulationFrame());
	}

	update() {
		if(this.destroyed) return;
		this.updateHostTransform();
		if(this.pendingOpeningSound && game.run) {
			this.pendingOpeningSound = false;
			if(this.active) this.playPortalSound('portal_open');
		}
		this.updatePortalOpening();
		this.syncDirectionArrowTransform();
		this.updateDirectionArrowVisibility();
		if(this.active && !this.bridge) this.tryLinkPortal();
		if(this.bridge) {
			this.bridge.update(
				Settings.physicsTimeStep * game.editor.editorSettingsObject.gameSpeed,
				simulationFrame(),
			);
		}
		this.updatePortalBuzz();
	}

	playPortalSound(sprite) {
		if(game.run && this.initialized && this.body) {
			AudioManager.playSFX(sprite, 0.3, 1, this.body.GetPosition(), this.fromPortalGun);
		}
	}

	updatePortalBuzz() {
		if(!game.run || !this.active || !this.bridge || !Settings.sfxOn) {
			this.stopPortalBuzz();
			return;
		}
		let occupied = false;
		for(const candidate of this.bridge.ghostWorld.transits) {
			if(candidate.source !== this && candidate.target !== this) continue;
			// An island cutting the entrance plane cuts the exit plane by the same
			// mirrored span, so both mouths are occupied by the same crossing.
			if(getPortalIntersectionSegments(Box2D, candidate.bodies, candidate.source).length) {
				occupied = true;
				break;
			}
		}
		const delta = Math.max(0, Math.min(0.1, game.editor.deltaTimeSeconds || 0));
		const target = occupied ? 0.035 : 0;
		this.buzzVolume += (target - this.buzzVolume) * (1 - Math.exp(-delta / 0.08));
		if(!occupied && this.buzzVolume < 0.001) {
			this.stopPortalBuzz();
			return;
		}
		AudioManager.playPrefabUniqueLoopSFX(this.audioKey, 'portal_buzz_loop', this.buzzVolume, 1, this.body.GetPosition());
	}

	stopPortalBuzz() {
		AudioManager.stopPrefabUniqueLoopSFX(this.audioKey, 'portal_buzz_loop');
		this.buzzVolume = 0;
	}

	cleanupPortal() {
		this.pendingOpeningSound = false;
		this.stopPortalBuzz();
		unregisterPair(this);
		this.detachBridge();
		this.frontSensorFixtureKeys.clear();
		this.sensorFixtures.clear();
		this.detachFromHost();
		if(this.body) {
			delete this.body.isPortalSurface;
			delete this.body.portalEndpoint;
		}
		this.destroyPortalOpening();
		this.openingElapsed = null;
		this.destroyPortalVisual();
		this.destroyDirectionArrow();
		this.initialized = false;
	}

	destroy() {
		if(this.destroyed) return;
		if(this.active) this.playPortalSound('portal_close');
		this.cleanupPortal();
		super.destroy();
	}

	reset() {
		this.cleanupPortal();
	}
}

const finishPortalTargetSelection = prefab => {
	prefab.class.linkPortalTarget(prefab.class.linkObjectTarget);
	delete prefab.class.linkObjectTarget;
	stopCustomBehaviour();
};

const selectPortalTarget = prefab => {
	if(prefab.class.getLinkedPortal()) {
		prefab.class.setLinkedPortal(null);
		game.editor.updateSelection();
		return;
	}
	game.editor.customPrefabMouseDown = () => finishPortalTargetSelection(prefab);
	game.editor.customDebugDraw = () =>
		drawObjectAdding(prefab, 'isPortalSurface', true);
	game.editor.customPrefabMouseMove = null;
};

const addPortalTargetGUI = (prefab, editData, targetFolder) => {
	const property = '__portalTarget';
	editData[property] = () => selectPortalTarget(prefab);
	targetFolder.add(editData, property).name(
		prefab.class.getLinkedPortal() ? 'unlink target portal' : 'select target portal',
	);
};

Portal.settings = Object.assign({}, Portal.settings, {
	pairId: '',
	linkedPortalId: null,
	active: true,
	color: '#0093ff',
	portalTarget: addPortalTargetGUI,
});

Portal.settingsOptions = Object.assign({}, Portal.settingsOptions, {
	active: true,
	color: '$color',
	portalTarget: '$custom',
});

PrefabManager.prefabLibrary.Portal = {
	json: '{"objects":[[0,0,0,0,"","portal",0,["#0093ff"],["#000"],[1],true,true,[[{"x":-0.75,"y":6},{"x":-0.75,"y":-6},{"x":0.75,"y":-6},{"x":0.75,"y":6}]],[1],9,[0],"",[1],true,false,false,[0.2],[0.05],false,false,true]]}',
	class: Portal,
	library: PrefabManager.LIBRARY_MOVEMENT,
};

export {Portal};
